// DetailPane — slide-over detail card for a search hit (airport / navaid
// / fix). Content is intentionally minimal in v1 — boss will fill in the
// shape later. For now we surface ident, name, coords, and a quick
// "Add to plan" / "Close" action so the map flow is usable end-to-end.
//
// 2026-05-17: when the tapped hit is already in the active plan, the
// pane offers an "Add hold at this waypoint" action that opens the
// existing HoldAtWaypointModal targeting the matching route index.
// Mirrors the PlanPanel row-level hold button so boss can build a
// hold without first opening the plan-panel drawer.

import { useState } from 'react';
import { Repeat } from '../shell/Icon';
import { formatLatLonDMM } from '../lib/coords';
import { SearchHit } from '../lib/search';
import { usePlanStore, PlanPointKind } from '../stores/planStore';
import InsertIntoRouteButtons from '../components/route/InsertIntoRouteButtons';
import DetailCard from '../components/DetailCard';
import { HoldAtWaypointModal } from '../routes/HoldAtWaypointModal';

interface Props {
  hit: SearchHit;
  onClose: () => void;
}

function hitToPlanKind(h: SearchHit): PlanPointKind {
  if (h.kind === 'airport') return 'airport';
  if (h.kind === 'fix') return 'fix';
  return (h.badge || '').toUpperCase().includes('NDB') ? 'ndb' : 'vor';
}

export default function DetailPane({ hit, onClose }: Props) {
  const route = usePlanStore((s) => s.route);
  // Match the hit against the active plan by ident + coords (4-decimal
  // tolerance). We don't need strict ident equality because a STAR/IAP
  // expansion might have placed a duplicate ident at a slightly
  // different coord (rare, but it happens with hold-in-lieu entries).
  // Coord-aware match keeps the action wired to the exact route row.
  const inPlanIdx = route.findIndex((p) =>
    p.ident === hit.ident
    && Math.abs(p.lat - hit.lat) < 0.0001
    && Math.abs(p.lon - hit.lon) < 0.0001,
  );
  const inPlan = inPlanIdx >= 0;
  const [holdOpen, setHoldOpen] = useState(false);

  const detailHeader = (
    <div className="min-w-0">
      <div className="font-mono text-cockpit-100 text-lg leading-tight">
        {hit.ident}
        {inPlan && (
          <span className="ml-2 text-[10px] font-semibold tracking-wider text-fuchsia-300">
            · IN PLAN
          </span>
        )}
      </div>
      <div className="text-xs text-cockpit-300 truncate">{hit.name}</div>
      {hit.sub && <div className="text-[11px] text-cockpit-400 truncate">{hit.sub}</div>}
    </div>
  );

  return (
    <>
      <DetailCard header={detailHeader} onClose={onClose} width="w-80">
        {/* DMM (Forest Service standard) per Wiley feedback 2026-05-09. */}
        <div className="text-[11px] font-mono text-cockpit-300">
          {formatLatLonDMM(hit.lat, hit.lon)}
        </div>

        {inPlan ? (
          <button
            type="button"
            onClick={() => setHoldOpen(true)}
            className="inline-flex items-center gap-1.5 rounded-md border border-fuchsia-400/40 bg-fuchsia-400/10 px-2.5 py-1.5 text-[12px] font-semibold text-fuchsia-200 hover:bg-fuchsia-400/20 transition-colors"
            aria-label={`Add hold at ${hit.ident}`}
          >
            <Repeat size={14} />
            Add hold at this waypoint
          </button>
        ) : (
          <InsertIntoRouteButtons
            point={{ ident: hit.ident, lat: hit.lat, lon: hit.lon, kind: hitToPlanKind(hit) }}
            onDone={onClose}
          />
        )}

        <p className="text-[10px] leading-snug text-cockpit-400">
          Detailed information (runways, frequencies, services, etc.) will appear here.
        </p>
      </DetailCard>

      {holdOpen && inPlanIdx >= 0 && (
        <HoldAtWaypointModal
          routeIndex={inPlanIdx}
          onClose={() => setHoldOpen(false)}
        />
      )}
    </>
  );
}
